import { Component, Input, Output, EventEmitter } from '@angular/core';
import { GenerationStatusResponse, ProcessStatus } from '../../models/statement-generator.models';

/**
 * Progress Monitor Component
 * Displays real-time progress of statement generation
 */
@Component({
  selector: 'app-statement-progress-monitor',
  template: `
    <div class="progress-monitor-container">
      <mat-card class="progress-card">
        <mat-card-content>
          <!-- Header -->
          <div class="progress-header">
            <div class="header-content">
              <mat-icon class="progress-icon" [class.spinning]="status.status === 'processing'">
                {{ getStatusIcon(status.status) }}
              </mat-icon>
              <div class="header-text">
                <h3 class="process-name">{{ status.name }}</h3>
                <p class="status-message">{{ status.message }}</p>
              </div>
            </div>
            @if (status.status !== 'processing') {
              <button mat-icon-button (click)="onClose.emit()" class="close-button">
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>

          <!-- Progress Bar -->
          <div class="progress-section">
            <div class="progress-info">
              <span class="progress-label">{{ getStatusLabel(status.status) }}</span>
              <span class="progress-percentage">{{ status.progress_percentage }}%</span>
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="status.progress_percentage"
              [color]="getProgressColor(status.status)">
            </mat-progress-bar>
          </div>

          <!-- Stats -->
          <div class="stats-section">
            <div class="stat-item">
              <mat-icon class="stat-icon">target</mat-icon>
              <div class="stat-content">
                <span class="stat-label">Target</span>
                <span class="stat-value">{{ status.target_statements }}</span>
              </div>
            </div>
            <div class="stat-item">
              <mat-icon class="stat-icon">check_circle</mat-icon>
              <div class="stat-content">
                <span class="stat-label">Generated</span>
                <span class="stat-value">{{ status.generated_statements_count }}</span>
              </div>
            </div>
            <div class="stat-item">
              <mat-icon class="stat-icon">schedule</mat-icon>
              <div class="stat-content">
                <span class="stat-label">Status</span>
                <span class="stat-value status-badge" [ngClass]="'status-' + status.status">
                  {{ status.status }}
                </span>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          @if (status.error_message) {
            <div class="error-section">
              <mat-icon class="error-icon">error</mat-icon>
              <p class="error-message">{{ status.error_message }}</p>
            </div>
          }

          <!-- Action Buttons -->
          <div class="action-section">
            @if (status.status === 'completed') {
              <button mat-raised-button color="primary" (click)="onViewStatements.emit()" class="view-button">
                <mat-icon>visibility</mat-icon>
                View {{ status.generated_statements_count }} Statements
              </button>
            }
            @if (status.status === 'failed') {
              <button mat-raised-button color="warn" (click)="onRetry.emit()" class="retry-button">
                <mat-icon>refresh</mat-icon>
                Retry Generation
              </button>
            }
          </div>

          <!-- Applied Toggles -->
          @if (status.applied_toggles) {
            <div class="toggles-section">
              <p class="toggles-title">Applied Settings:</p>
              <div class="toggles-list">
                @if (status.applied_toggles.strict_evidence_mode) {
                  <span class="toggle-chip">
                    <mat-icon>verified</mat-icon>
                    Strict Evidence Mode
                  </span>
                }
                @if (status.applied_toggles.minimize_off_label_tone) {
                  <span class="toggle-chip">
                    <mat-icon>policy</mat-icon>
                    Minimize Off-Label Tone
                  </span>
                }
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>
    </div>
  `,
  styles: [`
    .progress-monitor-container {
      padding: 20px;
      max-width: 800px;
      margin: 0 auto;
    }

    .progress-card {
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .progress-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 24px;

      .header-content {
        display: flex;
        align-items: center;
        gap: 16px;
        flex: 1;
      }

      .progress-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        color: #6c5ce7;

        &.spinning {
          animation: spin 2s linear infinite;
        }
      }

      .header-text {
        flex: 1;

        .process-name {
          margin: 0 0 4px 0;
          font-size: 20px;
          font-weight: 600;
          color: #2c3e50;
        }

        .status-message {
          margin: 0;
          color: #7f8c8d;
          font-size: 14px;
        }
      }

      .close-button {
        flex-shrink: 0;
      }
    }

    .progress-section {
      margin-bottom: 24px;

      .progress-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .progress-label {
          font-size: 14px;
          font-weight: 500;
          color: #495057;
        }

        .progress-percentage {
          font-size: 16px;
          font-weight: 600;
          color: #6c5ce7;
        }
      }
    }

    .stats-section {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 16px;
      margin-bottom: 24px;

      .stat-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: #f8f9fa;
        border-radius: 8px;

        .stat-icon {
          color: #6c5ce7;
          font-size: 24px;
          width: 24px;
          height: 24px;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
          gap: 2px;

          .stat-label {
            font-size: 12px;
            color: #7f8c8d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .stat-value {
            font-size: 18px;
            font-weight: 600;
            color: #2c3e50;

            &.status-badge {
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
              text-transform: uppercase;

              &.status-waiting { background: #ffc107; color: #000; }
              &.status-processing { background: #17a2b8; color: white; }
              &.status-completed { background: #28a745; color: white; }
              &.status-failed { background: #dc3545; color: white; }
            }
          }
        }
      }
    }

    .error-section {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      background: #fff5f5;
      border-left: 4px solid #e74c3c;
      border-radius: 8px;
      margin-bottom: 24px;

      .error-icon {
        color: #e74c3c;
        font-size: 24px;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
      }

      .error-message {
        margin: 0;
        color: #c0392b;
        font-size: 14px;
        line-height: 1.6;
      }
    }

    .action-section {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-bottom: 16px;

      button {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;

        mat-icon {
          font-size: 20px;
          width: 20px;
          height: 20px;
        }
      }
    }

    .toggles-section {
      padding-top: 16px;
      border-top: 1px solid #e9ecef;

      .toggles-title {
        margin: 0 0 8px 0;
        font-size: 12px;
        color: #7f8c8d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .toggles-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .toggle-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          background: #e8f4fd;
          border: 1px solid #b8d4e8;
          border-radius: 16px;
          font-size: 13px;
          color: #2c3e50;

          mat-icon {
            font-size: 16px;
            width: 16px;
            height: 16px;
            color: #6c5ce7;
          }
        }
      }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `],
  standalone: false
})
export class StatementProgressMonitorComponent {
  @Input() status!: GenerationStatusResponse;
  @Output() onClose = new EventEmitter<void>();
  @Output() onViewStatements = new EventEmitter<void>();
  @Output() onRetry = new EventEmitter<void>();

  getStatusIcon(status: ProcessStatus): string {
    switch (status) {
      case 'waiting': return 'schedule';
      case 'processing': return 'sync';
      case 'completed': return 'check_circle';
      case 'failed': return 'error';
      default: return 'help';
    }
  }

  getStatusLabel(status: ProcessStatus): string {
    switch (status) {
      case 'waiting': return 'Waiting to start...';
      case 'processing': return 'Generating statements...';
      case 'completed': return 'Generation completed!';
      case 'failed': return 'Generation failed';
      default: return 'Unknown status';
    }
  }

  getProgressColor(status: ProcessStatus): 'primary' | 'accent' | 'warn' {
    return status === 'failed' ? 'warn' : 'primary';
  }
}
